import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import BubblesContainer from './components/BubblesContainer';
import YouTubePlayer from './components/YouTubePlayer';
import MoreBubblesConfirmation from './components/MoreBubblesConfirmation';
import SpeedControl from './components/SpeedControl';
import BackgroundDecoration from './components/BackgroundDecoration';
import SoundToggle from './components/SoundToggle';
import NavigationControls from './components/NavigationControls';
import NotificationPopup from './components/NotificationPopup';
import { useBubbleManager } from './hooks/useBubbleManager';
import { useVideoManager } from './hooks/useVideoManager';
import { useSoundManager } from './hooks/useSoundManager';
import { thoughtsService } from './services/thoughtsService';
import { AdminProvider } from './contexts/AdminContext';
import AdminPage from './pages/AdminPage';
import ThoughtPage from './components/ThoughtPage';
import analyticsService from './services/analyticsService';
import './App.css';
import { requestPermission, onMessageListener } from "./firebase";

// Component that handles thoughtId from URL
function ThoughtBubblesApp({ thoughtId }) {
  const [bubbleSpeed, setBubbleSpeed] = useState(7);
  const [notificationPopup, setNotificationPopup] = useState(null);
  const [specificThought, setSpecificThought] = useState(null);

  useEffect(() => {
    requestPermission();
    onMessageListener().then((payload) => {
      console.log("Message received: ", payload);
      if (payload.notification) {
        setNotificationPopup({
          title: payload.notification.title,
          body: payload.notification.body,
          url: payload.notification.body // Using body as URL for now
        });
      }
    });
  }, []);

  // Load specific thought if thoughtId is provided
  useEffect(() => {
    if (thoughtId) {
      const loadSpecificThought = async () => {
        try {
          const thought = await thoughtsService.getThoughtById(thoughtId);
          setSpecificThought(thought);
        } catch (error) {
          console.error('Error loading specific thought:', error);
        }
      };
      loadSpecificThought();
    }
  }, [thoughtId]);
  
  const {
    isSoundEnabled,
    toggleSound,
    playWaterDropSound,
    ensureAudioReady
  } = useSoundManager();
  
  const {
    selectedVideo,
    isPlayerOpen,
    handleBubbleClick,
    closePlayer
  } = useVideoManager();

  const handleNotificationClose = () => {
    setNotificationPopup(null);
  };

  const handleNotificationVideoOpen = (url) => {
    // Create a video object similar to what handleBubbleClick expects
    const videoData = {
      id: 'notification-video',
      youtubeLink: url,
      startTime: 0
    };
    handleBubbleClick(videoData);
  };
  
  const {
    activeBubbles,
    showMoreBubbles,
    bubblesShown,
    allThoughts,
    canGoBack,
    handleBubbleComplete,
    showPreviousBubble,
    handleShowMoreBubbles,
    resumeBubbleProgression
  } = useBubbleManager(bubbleSpeed, playWaterDropSound, ensureAudioReady, isPlayerOpen, specificThought);

  return (
    <div className="App" onClick={ensureAudioReady}>
      <Header />
      
      <SoundToggle
        isSoundEnabled={isSoundEnabled}
        onToggle={toggleSound}
      />
      
      <NavigationControls
        canGoBack={canGoBack}
        onGoBack={showPreviousBubble}
      />
      
      <BubblesContainer
        activeBubbles={activeBubbles}
        onBubbleClick={handleBubbleClick}
        onBubbleComplete={handleBubbleComplete}
      />

      {/* Confirmation for more bubbles */}
      {showMoreBubbles && bubblesShown >= 4 && allThoughts.length > 4 && (
        <MoreBubblesConfirmation
          bubblesShown={bubblesShown}
          totalThoughts={allThoughts.length}
          onShowMore={handleShowMoreBubbles}
          onDecline={resumeBubbleProgression}
        />
      )}

      <YouTubePlayer
        videoId={selectedVideo?.id}
        isOpen={isPlayerOpen}
        onClose={closePlayer}
        videoUrl={selectedVideo?.youtubeLink}
        startTime={selectedVideo?.startTime}
        thought={selectedVideo?.thought}
        author={selectedVideo?.author}
      />

      <BackgroundDecoration />
      
      <SpeedControl
        bubbleSpeed={bubbleSpeed}
        onSpeedChange={setBubbleSpeed}
      />

      {/* Notification Popup */}
      {notificationPopup && (
        <NotificationPopup
          title={notificationPopup.title}
          body={notificationPopup.body}
          url={notificationPopup.url}
          onClose={handleNotificationClose}
          onOpenVideo={handleNotificationVideoOpen}
        />
      )}
    </div>
  );
}

// Main App component with routing
function App() {
  useEffect(() => {
    // Initialize Google Analytics
    analyticsService.init();
  }, []);

  return (
    <AdminProvider>
      <Router>
        <Routes>
          <Route path="/" element={<ThoughtBubblesApp />} />
          <Route path="/thought/:thoughtId" element={<ThoughtPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </Router>
    </AdminProvider>
  );
}


export default App;
